"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { isLetterboxed, isStandalone } from "@/lib/standalone-viewport";

/** Minimum time the splash stays up — avoids a sub-frame flash. */
const MIN_SHOW_MS = 500;
/** Hard cap: never hold the app hostage if the viewport won't settle. */
const MAX_SHOW_MS = 2500;
/** How often the settle check runs. */
const TICK_MS = 100;
/** Fade-out duration; keep in sync with the duration-* class below. */
const FADE_MS = 300;

/**
 * Branded launch splash for the installed PWA. Rendered on the server so it
 * covers the very first frame, but visible ONLY under
 * `@media (display-mode: standalone)` — browser visitors never see it.
 *
 * Purpose beyond branding: iOS standalone launches can open against a stale,
 * status-bar-short viewport (see AppHeightSync). The splash masks that settle
 * period — AppHeightSync measures and nudges underneath — and lifts once the
 * geometry is correct (or after a hard cap), so the deck is only ever revealed
 * at its final size.
 */
export function LaunchSplash() {
  const [fading, setFading] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (!isStandalone()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- CSS already hides it; this just drops the node.
      setGone(true);
      return;
    }

    const startedAt = Date.now();
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      if ((elapsed >= MIN_SHOW_MS && !isLetterboxed()) || elapsed >= MAX_SHOW_MS) {
        clearInterval(tick);
        setFading(true);
        fadeTimer = setTimeout(() => setGone(true), FADE_MS);
      }
    }, TICK_MS);

    return () => {
      clearInterval(tick);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, []);

  if (gone) return null;

  return (
    <div
      aria-hidden="true"
      className={cn(
        "fixed inset-0 z-[200] hidden items-center justify-center bg-ink",
        "[@media(display-mode:standalone)]:flex",
        "transition-opacity duration-300",
        fading && "opacity-0",
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- static brand SVG, intrinsic aspect */}
      <img
        src="/brand/logo-full.svg"
        alt=""
        className="w-64 drop-shadow-[0_0_40px_rgba(255,77,154,0.3)] sm:w-72"
        draggable={false}
      />
    </div>
  );
}
